import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  toRefs,
} from 'vue';

/**
 * Creates a context with provider and consumer functions
 * @param {string} id - The context identifier
 * @returns {Array} - Array containing [provider, consumer, contextId]
 */
export function useCreateContext(id) {
  const contextId = Symbol(id);
  const provider = value => provide(contextId, value);
  const consumer = fallback => inject(contextId, fallback);

  return [provider, consumer, contextId];
}

export const [PopoverProvider, usePopoverContext] =
  useCreateContext('PopoverContext');

export function usePopover(props, emit) {
  const {
    placement,
    offset: propOffset,
    isManual,
    isOpen: propIsOpen,
    triggerMode,
    hoverOpenDelay,
    hoverCloseDelay,
  } = toRefs(props);

  const isInside = ref(false);
  const isPopoverOpen = ref(false);
  const isOpen = computed(() =>
    isManual.value ? propIsOpen.value : isPopoverOpen.value
  );

  // Timeout IDs for hover delays
  const openTimeoutId = ref(null);
  const closeTimeoutId = ref(null);

  const trigger = ref(null);
  const content = ref(null);
  const { floatingStyles, placement: floatingPlacement } = useFloating(
    trigger,
    content,
    {
      placement,
      middleware: [offset(propOffset.value), flip(), shift()],
      whileElementsMounted(...args) {
        const cleanup = autoUpdate(...args, { animationFrame: true });
        return cleanup;
      },
    }
  );

  function handleClickOutside(event) {
    // If the mouse is inside the popover, don't close it
    if (isInside.value) return;

    const shouldClose =
      isOpen.value &&
      trigger.value &&
      content.value &&
      !trigger.value?.contains(event.target) &&
      !content.value?.contains(event.target);

    if (shouldClose && !isInside.value) {
      closePopover();
      emit('click-outside');
    }
  }

  function handleEscKey(event) {
    if (isOpen.value && event.key === 'Escape') {
      closePopover();
    }
  }

  function registerEventListeners() {
    nextTick(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    });
  }

  function destroyEventListeners() {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleEscKey);
  }

  function clearTimeouts() {
    if (openTimeoutId.value) {
      clearTimeout(openTimeoutId.value);
      openTimeoutId.value = null;
    }
    if (closeTimeoutId.value) {
      clearTimeout(closeTimeoutId.value);
      closeTimeoutId.value = null;
    }
  }

  function openPopover() {
    clearTimeouts();
    isPopoverOpen.value = true;

    nextTick(() => {
      emit('open');
      registerEventListeners();
    });
  }

  function closePopover() {
    clearTimeouts();
    isPopoverOpen.value = false;

    emit('close');

    destroyEventListeners();
  }

  function scheduleOpenPopover(delay = 0) {
    // Clear any pending close timeout
    if (closeTimeoutId.value) {
      clearTimeout(closeTimeoutId.value);
      closeTimeoutId.value = null;
    }

    // If already scheduled to open, don't schedule again
    if (openTimeoutId.value) return;

    if (delay === 0) {
      openPopover();
    } else {
      openTimeoutId.value = setTimeout(() => {
        openPopover();
      }, delay);
    }
  }

  function scheduleClosePopover(delay = 0) {
    // Clear any pending open timeout
    if (openTimeoutId.value) {
      clearTimeout(openTimeoutId.value);
      openTimeoutId.value = null;
    }

    // If already scheduled to close, don't schedule again
    if (closeTimeoutId.value) return;

    if (delay === 0) {
      closePopover();
    } else {
      closeTimeoutId.value = setTimeout(() => {
        closePopover();
      }, delay);
    }
  }

  // Clean up event listeners and timeouts when component is destroyed
  onBeforeUnmount(() => {
    clearTimeouts();
    destroyEventListeners();
  });

  return {
    isInside,
    isOpen,
    trigger,
    content,
    floatingStyles,
    floatingPlacement,
    openPopover,
    closePopover,
    scheduleOpenPopover,
    scheduleClosePopover,
  };
}
